import Chart, { ChartEvent, Plugin } from 'chart.js/auto';

type TimeWeightedReturnsOptions = {
    lineWidth: number;
    lfl?: number;
    nrl?: number;
    nru?: number;
    lfu?: number;
    normalRange?: number;
    lflColor?: string;
    nrlColor?: string;
    nruColor?: string;
    lfuColor?: string;
    normalRangeColor?: string;
};

export const TIME_WEIGHTED_RETURNS_PLUGIN: Plugin<'bar' | 'line'> = {
    id: 'timeWeightedReturns',
    defaults: {
        lineWidth: 30,
        lfl: undefined,
        nrl: undefined,
        nru: undefined,
        lfu: undefined,
        normalRange: undefined,
        lflColor: '#d4ebffB3',
        nrlColor: '#84aacbB3',
        nruColor: '#84aacbB3',
        lfuColor: '#d4ebffB3',
        normalRangeColor: '#3b6e9aB3'
    } as TimeWeightedReturnsOptions,
    afterInit: (chart: Chart): void => {
        // @ts-expect-error expected ignore
        chart.timeWeightedReturns = {
            x: 0
        };
    },
    afterEvent: (
        chart: Chart,
        args: { event?: ChartEvent; inChartArea?: boolean }
    ): void => {
        const { inChartArea } = args;
        const { x } = args.event!;

        // @ts-expect-error expected ignore
        chart.timeWeightedReturns = { x, draw: inChartArea };
        chart.draw();
    },
    beforeDatasetsDraw: (
        chart: Chart,
        _: never,
        options: TimeWeightedReturnsOptions
    ): void => {
        const { ctx } = chart;
        const { top, bottom } = chart.chartArea;

        if (
            // @ts-expect-error expected ignore
            !chart.timeWeightedReturns ||
            !options.lfl ||
            !options.lfu ||
            !options.nrl ||
            !options.nru ||
            !options.normalRange
        )
            return;
        // @ts-expect-error expected ignore
        const { x, draw } = chart.timeWeightedReturns;
        if (!draw) return;

        ctx.save();

        // We need to add five boxes to the group...
        // 1. Top -> LFU
        // 2. LFU -> NRU
        // 3. NRU -> NRL
        // 4. NRL -> LFL
        // 5. LFL -> Bottom
        let currentY = top;

        const SCALED_VALUES: number[] = scaleValuesProportionally(
            Object.values({
                lfu: options.lfu,
                nru: options.nru,
                normalRange: options.normalRange,
                nrl: options.nrl,
                lfl: options.lfl
            }),
            bottom - top
        );

        for (const [index, height] of SCALED_VALUES.entries()) {
            ctx.beginPath();
            ctx.lineWidth = options.lineWidth;

            switch (index) {
                case 1:
                    ctx.strokeStyle = options.nrlColor!;
                    break;
                case 2:
                    ctx.strokeStyle = options.normalRangeColor!;
                    break;
                case 3:
                    ctx.strokeStyle = options.nrlColor!;
                    break;
                case 4:
                    ctx.strokeStyle = options.lflColor!;
                    break;
                case 0:
                default:
                    ctx.strokeStyle = options.lfuColor!;
                    break;
            }

            ctx.moveTo(x, currentY);
            ctx.lineTo(x, currentY + height);
            ctx.stroke();

            currentY = currentY + height;
        }

        ctx.restore();
    }
};

function scaleValuesProportionally(values: number[], chartHeight: number) {
    const totalValue = values.reduce((sum, v) => sum + v, 0);

    return values.map((v) => (v / totalValue) * chartHeight);
}
